import { Download, Printer } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { Button } from "../atoms/Button";
import { Divider } from "../atoms/Divider";
import { Hash } from "../atoms/Hash";
import { Heading } from "../atoms/Heading";
import { KeyValue } from "../atoms/KeyValue";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

export type ReportKind = "event" | "loan" | "batch";

const KIND_LABELS: Record<ReportKind, string> = {
	event: "Event Report",
	loan: "Loan Audit Report",
	batch: "Batch Report",
};

const DEFAULT_VERDICT: Record<ReportKind, string> = {
	event:
		"This event is authentic and recorded on an independent public ledger.",
	loan: "This loan's records are authentic and recorded on an independent public ledger.",
	batch:
		"These records are authentic and recorded on an independent public ledger.",
};

export interface ReportPreviewProps {
	kind: ReportKind;
	target: string;
	metadata: Record<string, string>;
	/** Plain-language verdict shown at the top. Falls back to a kind-based default. */
	verdict?: string;
	proofData?: { hashes: string[]; root: string } | null;
	onDownload?: () => void;
	onPrint?: () => void;
	className?: string;
}

function ReportPreview({
	kind,
	target,
	metadata,
	verdict,
	proofData,
	onDownload,
	onPrint,
	className,
}: ReportPreviewProps): ReactNode {
	const verdictText = verdict ?? DEFAULT_VERDICT[kind];

	return (
		<div
			className={cn(
				"flex flex-col gap-4 rounded-lg border border-border bg-card p-6",
				className,
			)}
			aria-label="Report preview"
		>
			<div className="flex items-start justify-between gap-4">
				<div className="flex flex-col gap-1">
					<Badge variant="secondary" className="w-fit">
						{KIND_LABELS[kind]}
					</Badge>
					<Heading size="lg">{target}</Heading>
				</div>
				<div className="flex gap-2">
					{onPrint && (
						<Button variant="outline" size="sm" onClick={onPrint}>
							<Printer className="mr-1 h-4 w-4" aria-hidden="true" />
							Print
						</Button>
					)}
					{onDownload && (
						<Button size="sm" onClick={onDownload}>
							<Download className="mr-1 h-4 w-4" aria-hidden="true" />
							Download
						</Button>
					)}
				</div>
			</div>

			<Text size="sm" weight="medium">
				{verdictText}
			</Text>

			<Divider />

			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
				{Object.entries(metadata).map(([key, value]) => (
					<KeyValue key={key} label={key} value={value} />
				))}
			</div>

			{proofData && (
				<>
					<Divider />
					<details className="text-xs">
						<summary className="cursor-pointer select-none text-mutedForeground">
							Technical details (proof hashes)
						</summary>
						<div className="mt-3 flex flex-col gap-2">
							<ul className="flex flex-col gap-1">
								{proofData.hashes.map((h, i) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: proof hashes are positional; hash value itself would be more correct but duplicates are possible in tests
									<li key={i} className="flex items-center gap-2">
										<span className="text-xs text-mutedForeground w-8 shrink-0">
											{i + 1}.
										</span>
										<Hash value={h} length={8} />
									</li>
								))}
							</ul>
							<div className="flex items-center gap-2">
								<span className="text-xs text-mutedForeground">Root:</span>
								<Hash value={proofData.root} />
							</div>
						</div>
					</details>
				</>
			)}
		</div>
	);
}
ReportPreview.displayName = "ReportPreview";

export default ReportPreview;
export { ReportPreview };
